import { useCallback, useEffect, useRef, useState } from "react";
import MarkDownWithOption from "../ui/MarkDownWithOption";
import {
  createOrUpdateSeasonChallenge,
  getChallengeDetails,
  getSeasonSchedule,
  Team,
  updateLeagueFeedByChallengeField,
} from "./firestoreQuery";
import { useNavigate, useParams } from "react-router";
import defaultTeamImage from "../../assets/images/defaultTeamPic.svg";
import { useAuth } from "../../contexts/AuthContext";
import Cropper from "react-easy-crop";
import { cropImageFile } from "../utils/cropImage";
import { validateImageFile } from "../utils/imageValidation";
import { resizeImage } from "../utils/resizeImage";
import { uploadImage } from "../../services/firebase";
import { SectionLoader } from "../ui/SectionLoader";
import { set } from "lodash";

export interface IWeek {
  weekID: string;
  weekName: string;
  weekStartDate: string;
  weekEndDate: string;
}

type IChallengeType = "weekly" | "season";

interface IAddEditChallengeProps {
  leagueData: any;
  setSelectedTab: React.Dispatch<React.SetStateAction<number>>;
  setCreatedChallengeId: React.Dispatch<React.SetStateAction<string | null>>;
  createdChallengeId: string | null;
}

const AddEditChallenge: React.FC<IAddEditChallengeProps> = ({
  leagueData,
  setSelectedTab,
  setCreatedChallengeId,
  createdChallengeId,
}) => {
  const navigate = useNavigate();
  const user = useAuth().user;
  const { challengeId } = useParams();
  const [challengeType, setChallengeType] = useState<IChallengeType>("weekly");
  const [isMultipleWeeks, setIsMultipleWeeks] = useState<boolean>(false);
  const [isTeamRequirement, setIsTeamRequirement] = useState<boolean>(false);
  const [whatIsTheChallenge, setWhatIsTheChallenge] = useState<string>("");
  const [weeks, setWeeks] = useState<IWeek[] | null>(null);
  const [seasonStartDate, setSeasonStartDate] = useState<string | null>(null);
  const [seasonEndDate, setSeasonEndDate] = useState<string | null>(null);
  const [selectedStartingWeek, setSelectedStartingWeek] = useState<number>();
  const [selectedEndingWeek, setSelectedEndingWeek] = useState<number>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const [teamSelectionError, setTeamSelectionError] = useState<string>("");
  const [weekSelectionError, setWeekSelectionError] = useState<string>("");
  const [challengeDescriptionError, setChallengeDescriptionError] =
    useState<string>("");
  const [challengeTitle, setChallengeTitle] = useState<string>("");
  const [challengeTitleError, setChallengeTitleError] = useState<string>("");
  const [challengeCreatedBy, setChallengeCreatedBy] = useState<string>("");
  const [showCropper, setShowCropper] = useState(false);
  const [tempImageUrl, setTempImageUrl] = useState<string | null>(null);
  const [croppedArea, setCroppedArea] = useState<any>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const challengeImageRef = useRef<HTMLInputElement>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [challengeImage, setChallengeImage] = useState<File | null>(null);
  const [loadingCropper, setLoadingCropper] = useState(false);
  const weekSelectRef = useRef<HTMLDivElement>(null);
  const [existingChallengeImage, setExistingChallengeImage] = useState<
    string | null
  >(null);
  const [challengeStatus, setChallengeStatus] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Function to calculate the current week based on today's date
  const calculateWeek = (weeksData: IWeek[], offset?: number) => {
    const currentDate = new Date();
    let weekIndex = 1;

    for (let i = 0; i < weeksData.length; i++) {
      const weekStartDate = new Date(weeksData[i].weekStartDate);
      const weekEndDate = new Date(weeksData[i].weekEndDate);

      if (currentDate >= weekStartDate && currentDate <= weekEndDate) {
        weekIndex = parseInt(weeksData[i].weekID) + 1; // adding 1 to move to next week
        break;
      } else if (currentDate < weekStartDate) {
        weekIndex = parseInt(weeksData[i].weekID) + 1; // adding 1 to move to next week
        break;
      }
    }
    // offset is used to get the next week for ending week selection
    return offset ? weekIndex + offset : weekIndex;
  };

  // Fetch season weeks
  const getAllSeasonWeeks = async () => {
    try {
      setIsLoading(true);
      const res = await getSeasonSchedule();
      const weeksData = res?.weeks ?? null;
      const seasonStartDate = res?.season?.seasonStartDate ?? null;
      const seasonEndDate = res?.season?.seasonEndDate ?? null;
      setWeeks(weeksData);
      setSeasonStartDate(seasonStartDate);
      setSeasonEndDate(seasonEndDate);

      // Set default weeks based on current date if not in edit mode
      if (weeksData && !challengeId) {
        const currentWeek = calculateWeek(weeksData);
        setSelectedStartingWeek(currentWeek);
        setSelectedEndingWeek(currentWeek + 1);
      }
    } catch (error) {
      console.error("Error fetching season schedule:", error);
      setWeeks(null);
      setSeasonStartDate(null);
      setSeasonEndDate(null);
    } finally {
      setIsLoading(false);
    }
  };

  const getAllChallengeDetails = async () => {
    try {
      setIsLoading(true);
      if (!challengeId && !createdChallengeId) {
        setChallengeType("weekly");
        setWhatIsTheChallenge("");
        setChallengeTitle("");
        setIsMultipleWeeks(false);
        setIsTeamRequirement(false);
        setSelectedStartingWeek(1);
        setSelectedEndingWeek(2);
        setSelectedTeamIds([]); // Clear for new challenge
        setChallengeStatus("draft");
        return;
      }
      const res = await getChallengeDetails(
        leagueData?.id,
        challengeId ?? createdChallengeId ?? ""
      );
      setChallengeType(res?.challengeType ?? "weekly");
      setWhatIsTheChallenge(res?.challengeDescription ?? "");
      setChallengeTitle(res?.challengeTitle ?? "");
      setIsMultipleWeeks(res?.isMultipleWeeks ?? false);
      setIsTeamRequirement(res?.isTeamRequirement ?? false);
      setSelectedStartingWeek(
        res?.selectedStartingWeek ?? calculateWeek(weeks ?? [])
      );
      setSelectedEndingWeek(
        res?.selectedEndingWeek ?? calculateWeek(weeks ?? [], 1)
      );
      setImagePreview(res?.challengeImageUrl || null);
      setExistingChallengeImage(res?.challengeImageUrl || null);
      setChallengeCreatedBy(res?.createdBy || "");
      setChallengeStatus(res?.challengeCreationStatus || "draft");
      // Set selected team IDs based on the challenge data
      if (res?.isTeamRequirement && res?.teams) {
        const teamIds = res.teams
          .map((team: Team) => team.createdBy)
          .filter(Boolean);
        setSelectedTeamIds(teamIds);
      } else {
        setSelectedTeamIds([]);
      }
    } catch (error) {
      console.error("Error fetching challenge details:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch all season weeks and challenge details on component mount or when leagueData changes
  useEffect(() => {
    getAllSeasonWeeks();
    getAllChallengeDetails();
    if (
      challengeId && // editing mode
      challengeCreatedBy && // challenge creator is known
      challengeCreatedBy !== user?.uid // not the current user
    ) {
      return navigate(`/${leagueData.leagueUrl}/challenges`);
    }
  }, [
    leagueData,
    challengeCreatedBy,
    challengeId,
    user?.uid,
    createdChallengeId,
  ]);

  // Add a function to handle team selection
  const handleTeamSelection = (teamId: string, isSelected: boolean) => {
    // Clear error when user makes a selection
    if (teamSelectionError) {
      setTeamSelectionError("");
    }
    if (isSelected) {
      setSelectedTeamIds((prev) => [...prev, teamId]);
    } else {
      setSelectedTeamIds((prev) => prev.filter((id) => id !== teamId));
    }
  };

  // Update the effect to manage teams based on isTeamRequirement
  useEffect(() => {
    if (!isTeamRequirement) {
      // If no team requirement, use all teams
      const modifiedTeams =
        leagueData?.teams?.map((team: Team) => ({
          // ...team,
          createdBy: team.createdBy,
          hasJoined: team.createdBy === user?.uid,
        })) ?? [];
      setTeams(modifiedTeams);
    } else {
      // If team requirement is enabled, filter selected teams
      // Always include the challenge creator's team
      const allSelectedIds = [...selectedTeamIds];
      if (user?.uid && !allSelectedIds.includes(user.uid)) {
        allSelectedIds.push(user.uid);
      }
      // Filter selected teams
      const selectedTeams =
        leagueData?.teams?.filter((team: Team) =>
          allSelectedIds.includes(team?.createdBy ?? "")
        ) ?? [];
      const modifiedTeams = selectedTeams.map((team: Team) => ({
        // ...team,
        createdBy: team.createdBy,
        hasJoined: team.createdBy === user?.uid,
      }));
      setTeams(modifiedTeams);
    }
  }, [isTeamRequirement, selectedTeamIds, leagueData?.teams, user?.uid]);

  // Add effect to automatically include league creator when team requirement is enabled
  useEffect(() => {
    if (isTeamRequirement && user?.uid) {
      setSelectedTeamIds((prev) => {
        if (!prev.includes(user?.uid)) {
          return [...prev, user?.uid];
        }
        return prev;
      });
    }
  }, [isTeamRequirement, user?.uid]);
  // Ref to scroll to week selection on error
  useEffect(() => {
    if (weekSelectionError && weekSelectRef.current) {
      weekSelectRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [weekSelectionError]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear previous errors
    setTeamSelectionError("");
    setWeekSelectionError("");
    setChallengeDescriptionError("");
    setChallengeTitleError("");

    //validation for season challenge
    const weekStartDate = weeks?.find(
      (week) => Number(week.weekID) === Number(selectedStartingWeek)
    )?.weekStartDate;
    console.log(weekStartDate);
    console.log(selectedStartingWeek);
    if (
      challengeType === "weekly" &&
      new Date(weekStartDate || "") < new Date()
    ) {
      setWeekSelectionError("Selected week cannot be in the past."); // force multiple weeks to false for season challenge
      return;
    }

    // validate that starting week is less than ending week
    if (
      challengeType === "weekly" &&
      isMultipleWeeks &&
      selectedStartingWeek !== undefined &&
      selectedEndingWeek !== undefined &&
      selectedStartingWeek >= selectedEndingWeek
    ) {
      setWeekSelectionError("Ending week must be greater than starting week.");
      return;
    }

    // Validate challenge description
    if (!whatIsTheChallenge.trim()) {
      setChallengeDescriptionError("Challenge description is required.");
      return;
    }

    // Validate challenge title
    if (!challengeTitle.trim()) {
      setChallengeTitleError("Challenge title is required.");
      return;
    }

    // Validate team selection if team requirement is enabled
    if (isTeamRequirement && selectedTeamIds.length === 0) {
      setTeamSelectionError("Please select at least one team to challenge.");
      return;
    }

    // Handle challenge image upload
    let challengeImageUrl = imagePreview ? existingChallengeImage ?? "" : "";
    if (challengeImage) {
      try {
        challengeImageUrl = await uploadImage(challengeImage);
      } catch (error) {
        console.error("Error uploading challenge image:", error);
      }
    }
    setIsSubmitting(true);

    // Handle form submission logic here
    const payload: any = {
      leagueId: leagueData?.id,
      challengeType,
      isMultipleWeeks,
      isTeamRequirement,
      challengeDescription: whatIsTheChallenge,
      challengeTitle,
      challengeImageUrl,
      challengeCreationStatus: challengeStatus || "draft",
      teams: teams,
      createdBy: user?.uid || "",
    };

    if (challengeType === "weekly") {
      payload.selectedStartingWeek = Number(selectedStartingWeek);
      payload.selectedEndingWeek = isMultipleWeeks
        ? Number(selectedEndingWeek)
        : Number(selectedStartingWeek);
      payload.selectedStartingWeekDate = weeks?.find(
        (week) => Number(week.weekID) === Number(selectedStartingWeek)
      )?.weekStartDate;
      payload.selectedEndingWeekDate = isMultipleWeeks
        ? weeks?.find(
            (week) => Number(week.weekID) === Number(selectedEndingWeek)
          )?.weekEndDate
        : weeks?.find(
            (week) => Number(week.weekID) === Number(selectedStartingWeek)
          )?.weekEndDate;
    } else {
      // For season challenge, set weeks to null
      payload.isMultipleWeeks = false;
      payload.selectedStartingWeek = null;
      payload.selectedEndingWeek = null;
      payload.selectedStartingWeekDate = null;
      payload.selectedEndingWeekDate = null;
      payload.seasonStartDate = seasonStartDate;
      payload.seasonEndDate = seasonEndDate;
    }

    // Update the teams join status for edit mode
    if (challengeId) {
      const creatorId = challengeCreatedBy || user?.uid;
      payload.teams = teams.map((team) => ({
        ...team,
        hasJoined: team.createdBy === creatorId,
      }));
      console.log("Payload teams in edit mode:", payload.teams);
    }

    try {
      setIsLoading(true);
      const res = await createOrUpdateSeasonChallenge(
        payload,
        challengeId ?? createdChallengeId ?? ""
      );
      setCreatedChallengeId(res.challengeId || null);
      // Update feed if editing existing challenge
      if (challengeId) {
        const feedData = {
          challengeTitle: payload?.challengeTitle,
          challengeDescription: payload?.challengeDescription,
          challengeImageUrl: payload?.challengeImageUrl,
        };
        await updateLeagueFeedByChallengeField(
          leagueData.id,
          challengeId,
          feedData
        );
      }
    } catch (error) {
      console.error("Error creating/updating challenge:", error);
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
    }
    setSelectedTab(2);
  };

  // Handlers for week selection
  const handleSelectStartingWeek = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const selectedWeekID = e.target.value;
    setSelectedStartingWeek(+selectedWeekID);
  };
  const handleSelectEndingWeek = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedWeekID = e.target.value;
    setSelectedEndingWeek(+selectedWeekID);
  };

  useEffect(() => {
    if (challengeId) return; // Don't override title in edit mode
    let titleDefaultValue = "";
    if (challengeType === "season") {
      titleDefaultValue = `Season Challenge - ${new Date(
        seasonStartDate ?? ""
      ).getFullYear()}`;
    } else {
      titleDefaultValue = `Weekly Challenge - Week ${selectedStartingWeek}`;
      if (isMultipleWeeks) {
        titleDefaultValue += ` thru ${selectedEndingWeek}`;
      }
    }
    setChallengeTitle(titleDefaultValue);
  }, [
    seasonStartDate,
    challengeId,
    challengeType,
    selectedStartingWeek,
    selectedEndingWeek,
    isMultipleWeeks,
  ]);

  const onCropComplete = useCallback((_: any, croppedPixels: any) => {
    setCroppedArea(croppedPixels);
  }, []);

  const handleCropConfirm = useCallback(async () => {
    if (!tempImageUrl || !croppedArea) return;
    const cropped = await cropImageFile(tempImageUrl, croppedArea);
    if (cropped) {
      setChallengeImage(cropped);
      setImagePreview(URL.createObjectURL(cropped));
    }
    setShowCropper(false);
  }, [tempImageUrl, croppedArea]);

  const handleImageChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const validationError = validateImageFile(file);
      if (validationError) {
        alert(`${validationError}`);
        return;
      }
      setLoadingCropper(true);
      try {
        const { resizedUrl } = await resizeImage(file);
        setTempImageUrl(resizedUrl);
        setShowCropper(true);
      } finally {
        setLoadingCropper(false);
      }
    },
    []
  );

  const handleRemoveImage = () => {
    setChallengeImage(null);
    setImagePreview(null);
    if (challengeImageRef.current) challengeImageRef.current.value = "";
  };

  // Add a new derived loading state for edit mode
  const isEditMode = !!challengeId;
  const isEditLoading =
    isEditMode &&
    (isLoading || (!challengeCreatedBy && challengeCreatedBy !== ""));

  // Return loading state
  if (isEditLoading || isLoading) {
    return <SectionLoader />;
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="yourLeagueHeader">
        <div className="headingItem">
          {challengeId ? "Edit" : "Create"} a Challenge
        </div>
      </div>
      <div className="shadowBlk">
        <div className="blkHeading">Challenge Style</div>
        <div className="btnWrapInline bottomGap1">
          <button
            className={`btnStyle${
              challengeType === "weekly" ? " btnFilled" : ""
            }`}
            onClick={() => setChallengeType("weekly")}
            type="button"
            disabled={isEditMode && challengeType !== "weekly"}
          >
            WEEKLY
          </button>
          <button
            className={`btnStyle${
              challengeType === "season" ? " btnFilled" : ""
            }`}
            onClick={() => setChallengeType("season")}
            type="button"
            disabled={isEditMode && challengeType !== "season"}
          >
            SEASON
          </button>
        </div>
        {challengeType === "weekly" && (
          <>
            <div className="formGroup" ref={weekSelectRef}>
              <label htmlFor="weekStart">
                Select a Week <span className="reqTxt">Required</span>
              </label>
              <select
                className="formSelect"
                value={selectedStartingWeek ?? 1}
                onChange={(e) => {
                  handleSelectStartingWeek(e);
                  // Clear error when user selects a week
                  if (weekSelectionError) {
                    setWeekSelectionError("");
                  }
                }}
              >
                {(challengeId
                  ? weeks
                  : weeks?.filter(
                      (week) => parseInt(week.weekID) >= calculateWeek(weeks)
                    )
                )?.map((week) => (
                  <option key={week.weekID} value={week.weekID}>
                    {week.weekName}
                  </option>
                ))}
              </select>
            </div>
            <div className="switchWrap bottomGap1">
              <label>Multiple weeks?</label>
              <label className="switchOption">
                <input
                  type="checkbox"
                  checked={isMultipleWeeks}
                  onChange={(e) => setIsMultipleWeeks(e.target.checked)}
                />
                <span
                  className={`customCheck ${
                    isMultipleWeeks ? "haveChecked" : ""
                  }`}
                >
                  <span
                    className="checkCircle"
                    style={{
                      transform: isMultipleWeeks
                        ? "translateX(26px)"
                        : "translateX(0)",
                    }}
                  />
                </span>
              </label>
            </div>
            {isMultipleWeeks && (
              <div className="formGroup">
                <label htmlFor="leagueName">
                  Ending Week <span className="reqTxt">Required</span>
                </label>
                <select
                  className="formSelect"
                  value={selectedEndingWeek ?? 2}
                  onChange={(e) => {
                    handleSelectEndingWeek(e);
                    // Clear error when user selects a week
                    if (weekSelectionError) {
                      setWeekSelectionError("");
                    }
                  }}
                >
                  {(challengeId
                    ? weeks
                    : weeks?.filter(
                        (week) =>
                          parseInt(week.weekID) >= calculateWeek(weeks, 1) // Ensure at least one week after current
                      )
                  )?.map((week) => (
                    <option key={week.weekID} value={week.weekID}>
                      {week.weekName}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {weekSelectionError && (
              <div className="errorText">{weekSelectionError}</div>
            )}
          </>
        )}
      </div>
      <div className="shadowBlk">
        <div className="blkHeading">Challenge</div>
        <div className="formGroup">
          <label htmlFor="leagueName">
            Challenge Title <span className="reqTxt">Required</span>
          </label>
          <input
            type="text"
            id="challengeTitle"
            placeholder="Enter your challenge title"
            value={challengeTitle ?? ""}
            maxLength={100}
            onChange={(e) => {
              const val = e.target.value;
              // enforce max length (input already has maxLength) and clear error when typing
              if (val.length <= 100) {
                setChallengeTitle(val);
                setChallengeTitleError("");
              }
            }}
          />
          <div className="text-sm text-gray-500 mt-1">
            {(challengeTitle ?? "").length}/100
          </div>
          {challengeTitleError && (
            <div className="errorText">{challengeTitleError}</div>
          )}
        </div>
        <div className="formGroup">
          <label htmlFor="leagueName">
            What's the Challenge? <span className="reqTxt">Required</span>
          </label>
          <MarkDownWithOption
            value={whatIsTheChallenge}
            onChange={(value) => {
              setWhatIsTheChallenge(value ?? "");
              // Clear error when user starts typing
              if (challengeDescriptionError) {
                setChallengeDescriptionError("");
              }
            }}
          />
          {challengeDescriptionError && (
            <div className="errorText">{challengeDescriptionError}</div>
          )}
        </div>
        <div className="formGroup">
          {showCropper && tempImageUrl && (
            <div className="popupWrapper fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center">
              <div className="relative w-[90vw] max-w-2xl h-[90vh] bg-white rounded-lg overflow-hidden shadow-lg">
                <Cropper
                  image={tempImageUrl}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
                <div className="absolute bottom-0 left-0 w-full flex justify-between p-4 bg-white border-t">
                  <button
                    type="button"
                    onClick={() => setShowCropper(false)}
                    className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleCropConfirm}
                    className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                  >
                    Crop & Save
                  </button>
                </div>
              </div>
            </div>
          )}
          <div className="shadowBlk">
            <div className="blkHeading">📸 Challenge Image</div>
            <div className="uploadArea">
              {!imagePreview && (
                <>
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    ref={challengeImageRef}
                    onChange={handleImageChange}
                  />
                  <button
                    className="btnStyle btnHighlighted"
                    type="button"
                    onClick={() => challengeImageRef.current?.click()}
                  >
                    UPLOAD CHALLENGE IMAGE
                  </button>
                </>
              )}

              {imagePreview && (
                <div className="relative inline-block">
                  <img
                    src={imagePreview}
                    alt="Punishment Preview"
                    style={{
                      width: 200,
                      borderRadius: 8,
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      handleRemoveImage();
                    }}
                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
                    style={{ lineHeight: 1, fontSize: 16 }}
                    aria-label="Remove image"
                  >
                    ×
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="shadowBlk">
        <div className="blkHeading">Team Requirements</div>
        <div className="switchWrap bottomGap1">
          <label>Select specific teams to challenge?</label>
          <label className="switchOption">
            <input
              type="checkbox"
              checked={isTeamRequirement}
              onChange={(e) => {
                setIsTeamRequirement(e.target.checked);
                setTeamSelectionError("");
              }}
            />
            <span
              className={`customCheck ${
                isTeamRequirement ? "haveChecked" : ""
              }`}
            >
              <span
                className="checkCircle"
                style={{
                  transform: isTeamRequirement
                    ? "translateX(26px)"
                    : "translateX(0)",
                }}
              />
            </span>
          </label>
        </div>
        {/* Update the team list rendering with controlled checkboxes */}
        {isTeamRequirement && (
          <ul className="teamList">
            {leagueData?.teams &&
              leagueData?.teams?.map((team: Team) => (
                <li className="teamItem" key={team.createdBy}>
                  <div className="teamDetail">
                    <span className="teamPic">
                      <img src={team.imageUrl ?? defaultTeamImage} alt="" />
                    </span>
                    <span>{team.name}</span>
                  </div>
                  <input
                    type="checkbox"
                    checked={
                      selectedTeamIds.includes(team?.createdBy ?? "") ||
                      team?.createdBy === user?.uid
                    }
                    disabled={team?.createdBy === user?.uid}
                    onChange={(e) =>
                      handleTeamSelection(
                        team?.createdBy ?? "",
                        e.target.checked
                      )
                    }
                  />
                </li>
              ))}
          </ul>
        )}
        {teamSelectionError && (
          <div className="errorText">{teamSelectionError}</div>
        )}
      </div>
      <div className="btnWrap">
        <button
          type="button"
          className="btnStyle"
          onClick={() => {
            setSelectedTab(0);
            navigate(`/${leagueData.leagueUrl}/challenges`);
          }}
        >
          CANCEL
        </button>
        <button
          className="btnStyle btnFilled"
          type="submit"
          disabled={isSubmitting}
        >
          CONTINUE
        </button>
      </div>
    </form>
  );
};

export default AddEditChallenge;
